import { computed, watch } from "vue"
import { useStore } from "vuex"
import schoolYearRetention from "@/helpers/schoolYearRetention"

/**
 * School year options for filters. Years come from one place so every
 * dropdown stays in sync with the school's last_date_to_keep_data
 * (PASS-8131 / PASS-8136). School year rolls over on July 1st.
 */
export function useGradeYears() {
  const store = useStore()

  const activeSchool = computed(() => {
    return store?.getters?.["schools/activeSchool"] || null
  })

  const currentSchoolYearStart = computed(() => {
    return schoolYearRetention.getCurrentSchoolYearStartYear()
  })

  const priorSchoolYears = computed(() => {
    return schoolYearRetention.resolvePriorSchoolYears(
      activeSchool.value,
      currentSchoolYearStart.value
    )
  })

  const gradeYears = computed(() => {
    const years = []

    for (let i = priorSchoolYears.value; i >= 0; i--) {
      const startYear = currentSchoolYearStart.value - i
      const endYear = startYear + 1

      years.push({
        value: [startYear, endYear],
        label: `${startYear}-${endYear.toString().slice(-2)}`
      })
    }

    return years
  })

  const currentSchoolYear = computed(() => {
    return gradeYears.value[gradeYears.value.length - 1]
  })

  const hasSelectableGradeYears = computed(() => gradeYears.value.length > 1)

  const getMinDate = (schoolYear) => {
    if (schoolYear?.value) {
      return schoolYearRetention.clampMinDate(
        schoolYearRetention.getSchoolYearMinDate(schoolYear.value[0]),
        activeSchool.value
      )
    }
    return new Date()
  }

  const getMaxDate = (schoolYear) => {
    if (schoolYear?.value) {
      return new Date(schoolYear.value[1], 5, 30) // June 30th
    }
    return new Date()
  }

  const resolveSelectedGradeYear = (selectedGradeYear) => {
    return schoolYearRetention.resolveSelectedGradeYear(
      selectedGradeYear,
      gradeYears.value
    )
  }

  /**
   * Reacts to allowed-year changes (not user picks). The first argument is
   * only a getter for the current selection.
   */
  const watchSelectedGradeYear = (
    getSelected,
    setSelected,
    onSelectionChanged
  ) => {
    watch(
      () => gradeYears.value.map((year) => year.label).join("|"),
      () => {
        const selectedGradeYear = getSelected?.()
        const nextGradeYear = resolveSelectedGradeYear(selectedGradeYear)

        if (nextGradeYear?.label === selectedGradeYear?.label) {
          return
        }

        setSelected?.(nextGradeYear)
        onSelectionChanged?.(nextGradeYear)
      }
    )
  }

  return {
    gradeYears,
    currentSchoolYear,
    hasSelectableGradeYears,
    getMinDate,
    getMaxDate,
    resolveSelectedGradeYear,
    watchSelectedGradeYear
  }
}
